import classNames from "classnames";
import dayjs from "dayjs";
import { nanoid } from "nanoid";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CircularProgressbar } from "react-circular-progressbar";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { useDispatch } from "react-redux";
import { toast } from "sonner";
import { getTextID, getTextsContent, putTextsContent } from "../../../api/texts.service";
import { fetchCards } from "../../../app/cards/cards.slice";
import Icon from "../../../components/Icon";
import Modal from "../../../components/Modal";
import Popover from "../../../components/Popover";
import Switch from "../../../components/Switch";
import Chinese from "./components/Chinese";
import Questions from "./components/Questions";
import Translate from "./components/Translate";
import styles from "./Content2.module.sass";
import { LANG_LABELS, LEVEL_CHART_COLORS } from "../../../utils/constants";

const LANGS = ["en", "es", "fr", "ru", "pt"];

const NAVS = ["Content", "Questions"];

const normalizeChaptersResponse = (rawData) => {
  let parsedData = rawData;

  if (typeof parsedData === "string") {
    parsedData = JSON.parse(parsedData);
  }

  if (Array.isArray(parsedData)) return parsedData;
  if (Array.isArray(parsedData?.content)) return parsedData.content;
  if (parsedData && typeof parsedData === "object" && Array.isArray(parsedData?.sentences)) {
    return [parsedData];
  }

  return [];
};

const Content2 = ({ embedded = false, onRegisterSave, onContentSaveRef, filtersSlot, actionsSlot }) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const dispatch = useDispatch();

  const myRefs = useRef([]);
  const translateRefs = useRef([]);
  const videoRefs = useRef([]);
  const chaptersRef = useRef([]);

  const [chapters, setChapters] = useState([]);
  chaptersRef.current = chapters;

  const [chapterIndexSelected, setChapterIndexSelected] = useState(0);

  const [level, setLevel] = useState([]);

  const [dataTextLevel, setDataTextLevel] = useState("");

  const [visibleModal, setVisibleModal] = useState(false);
  const [textImport, setTextImport] = useState("");

  const [navSelected, setNavSelected] = useState("Content");

  const [viewLevel, setViewLevel] = useState(true);
  const [viewPinyin, setViewPinyin] = useState(true);

  const [loadingTranslation, setLoadingTranslation] = useState(false);
  const [confirmTranslateModalVisible, setConfirmTranslateModalVisible] = useState(false);
  const [selectedLevelFilter, setSelectedLevelFilter] = useState("ALL");

  const [questions, setQuestions] = useState([]);

  const [contentLoading, setContentLoading] = useState(true);
  const contentLoadedRef = useRef(false);

  const [activeTitleTab, setActiveTitleTab] = useState("en");

  const levelFilterOptions = useMemo(() => {
    const levelTitles = (level || []).map((c) => c?.title).filter(Boolean);
    return ["ALL", ...levelTitles];
  }, [level]);

  useEffect(() => {
    if (!levelFilterOptions.includes(selectedLevelFilter)) {
      setSelectedLevelFilter("ALL");
    }
  }, [levelFilterOptions, selectedLevelFilter]);

  const handleCycleLevelFilter = useCallback(() => {
    if (!levelFilterOptions.length) return;
    setViewLevel(true);
    setSelectedLevelFilter((prev) => {
      const idx = levelFilterOptions.indexOf(prev);
      const nextIndex = idx === -1 ? 0 : (idx + 1) % levelFilterOptions.length;
      return levelFilterOptions[nextIndex];
    });
  }, [levelFilterOptions]);

  const activeLevelFilterLabel = selectedLevelFilter === "ALL" ? t("common.all", "All") : selectedLevelFilter;

  // Un solo menú de tarjeta abierto en toda la vista (evita menús apilados)
  const [openCardPopover, setOpenCardPopover] = useState(null);
  // Fila con tooltip de card visible (vista embebida): subir z-index para que el tooltip no quede tapado por la fila siguiente
  const [hoveredTooltipSentenceIndex, setHoveredTooltipSentenceIndex] = useState(null);

  const anadirCapitulo = useCallback(() => {
    setChapters((prev) => [
      ...prev,
      { sentences: [], text: "", order: prev.length, processRawText: false },
    ]);
  }, []);

  const handleGetContent = useCallback(async () => {
    if (!id) return;
    setContentLoading(true);
    contentLoadedRef.current = false;
    try {
      const response = await getTextsContent({ _id: id });
      const chaptersRaw = normalizeChaptersResponse(response?.data);
      const responseParsed = chaptersRaw.map((c) => ({
        ...c,
        sentences: (c?.sentences || []).map((sentc) => {
          const translations = Array.isArray(sentc?.translations) ? [...sentc.translations] : [];
          LANGS.forEach((lang) => {
            if (!translations.some((tt) => tt.lang === lang)) {
              translations.push({ active: true, lang, trans: "" });
            }
          });
          return { ...sentc, id: nanoid(), translations };
        }),
      }));
      setChapters(responseParsed.length > 0 ? responseParsed : []);
    } catch (error) {
      console.error(`Error loading text content for ${id}`, error);
      toast.error(t("toast.thereWasError"));
      setChapters([]);
    } finally {
      setContentLoading(false);
      contentLoadedRef.current = true;
    }
  }, [id, t]);

  const handleGetTextLevel = useCallback(async () => {
    if (!id) return;
    const response = await getTextID("", id);
    if (response?.data) {
      setLevel(response.data?.levelText || []);
      setDataTextLevel(response.data);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      handleGetContent();
      handleGetTextLevel();
      dispatch(fetchCards({ take: 5, skip: 0 }));
    }
  }, [id, handleGetContent, handleGetTextLevel, dispatch]);

  const handlePutContent = useCallback(async ({ manual = false } = {}) => {
    // Prevent unintended writes when opening the editor: save must be explicitly triggered.
    if (!manual) return;
    if (!contentLoadedRef.current) return;
    const response = await putTextsContent(chaptersRef.current, id);
    if (response?.status === 200) {
      toast.success(t("toast.savedSuccessfully"));
      // No llamar handleGetContent aquí: evita bucle (load -> save -> load -> save...)
    } else {
      toast.error(t("toast.thereWasError"));
    }
  }, [id, t]);

  const runManualContentSave = useCallback(async () => {
    await handlePutContent({ manual: true });
  }, [handlePutContent]);

  useEffect(() => {
    if (embedded && onContentSaveRef) {
      onContentSaveRef(runManualContentSave, contentLoading);
      return () => onContentSaveRef(null, true);
    }
  }, [embedded, onContentSaveRef, runManualContentSave, contentLoading]);




  // syncHeights has been removed since we now use CSS grid stretch for aligning column heights.

  const AnalysisComponent = useMemo(
    () =>
      function AnalysisComponentInner() {
        return (
          <div className={styles.analysis}>
            <div>
              <div className={styles.analysisSecondary} style={{ textAlign: "end" }}>{dataTextLevel?.analysis?.words} {t("texts.words")}</div>
              <div style={{ textAlign: "end" }}>{dataTextLevel?.analysis?.characters} {t("texts.chineseChars")}</div>
            </div>
            <div
              style={{
                fontSize: 24,
                color: "#CE2121",
                justifyContent: "center",
                display: "flex",
                alignItems: "center",
                width: "50px",
                marginLeft: 20,
                height: "50px",
              }}
            >
              <CircularProgressbar
                width={50}
                height={50}
                value={dataTextLevel?.analysis?.characters}
                maxValue={dataTextLevel?.analysis?.words}
                text={dataTextLevel?.analysis?.grade}
                styles={{
                  path: { stroke: "#E94D4D", strokeLinecap: "butt" },
                  trail: { stroke: "#E8E8E8" },
                  text: { fill: "#CE2121", fontSize: "36px" },
                }}
              />
            </div>
          </div>
        );
      },
    [dataTextLevel?.analysis?.words, dataTextLevel?.analysis?.characters, dataTextLevel?.analysis?.grade, t]
  );

  const handleAddImportText = async () => {
    let translations = [];

    LANGS.forEach((lang) => {
      translations.push({
        active: true,
        lang,
        trans: "",
      });
    });

    const uniqueObjects = { cards: [], processRawText: true, text: textImport, translations, id: nanoid() };

    let chapters_ = chapters.map((capitulo, indice) => {
      if (indice === chapterIndexSelected) {
        return { ...capitulo, sentences: [...capitulo.sentences, uniqueObjects] };
      }
      return capitulo;
    });

    let response = await putTextsContent(chapters_, id);

    if (response) {
      toast.success(t("toast.textImported"));
      setVisibleModal(false);
      setTextImport("");
      handleGetContent();
    }
  };

  // eslint-disable-next-line no-unused-vars -- reserved for future "Translate all" feature
  const handleTranslateAll = async () => {
    let token = "";
    try {
      token = JSON.parse(localStorage?.getItem("user") || "{}")?.token || "";
    } catch (e) {
      token = "";
    }
    if (!token) {
      toast.error(t("common.error", "Error"));
      return;
    }

    setLoadingTranslation(true);
    const url = `${process.env.REACT_APP_API_URL}/translation/deepl`;
    const authorizationHeader = { Authorization: `Bearer ${token}` };
    const TRANS_LANGS = ["es", "pt", "fr", "ru"];

    const sourceChapters = chaptersRef.current || [];
    let chapters_ = sourceChapters.map((capitulo) => ({
      ...capitulo,
      sentences: (capitulo?.sentences || []).map((frase) => ({
        ...frase,
        translations: [...(frase?.translations || [])],
      })),
    }));

    try {
      for (let [indexChapter, chapter] of chapters_.entries()) {
        for (let [indexSentence, sentence] of (chapter?.sentences || []).entries()) {
          const sourceText = sentence?.translations?.find((c) => c?.lang === "en")?.trans;
          if (!sourceText) continue;
          for (let lang_ of TRANS_LANGS) {
            const response = await fetch(url, {
              method: "POST",
              body: JSON.stringify({ title: sourceText, lang: lang_ }),
              headers: { "Content-Type": "application/json", ...authorizationHeader },
            });
            const data = await response.json();
            const translation = data?.translations?.[0]?.text;
            if (!translation) continue;

            chapters_ = chapters_.map((capitulo, indice) => {
              if (indice !== indexChapter) return capitulo;
              return {
                ...capitulo,
                sentences: capitulo.sentences.map((frase, idx) => {
                  if (idx !== indexSentence) return frase;
                  return {
                    ...frase,
                    translations: frase.translations.map((traduccion) =>
                      lang_ === traduccion.lang
                        ? { ...traduccion, active: true, lang: lang_, trans: translation }
                        : traduccion
                    ),
                  };
                }),
              };
            });
          }
        }
      }
      setChapters(chapters_);
      toast.success(t("toast.translatedSuccessfully"));
    } catch (error) {
      toast.error(t("toast.thereWasError"));
    } finally {
      setLoadingTranslation(false);
    }
  };

  const editarTraduccionDeFrase = useCallback((indiceCapitulo, indiceFrase, idioma, nuevaTraduccion) => {
    setChapters((prev) =>
      prev.map((capitulo, indice) =>
        indice === indiceCapitulo
          ? {
              ...capitulo,
              sentences: capitulo.sentences.map((frase, idx) =>
                idx === indiceFrase
                  ? {
                      ...frase,
                      translations: frase.translations.map((t) =>
                        t.lang === idioma ? { ...t, ...nuevaTraduccion } : t
                      ),
                    }
                  : frase
              ),
            }
          : capitulo
      )
    );
  }, []);

  const traducirTextoFrase = useCallback(async (indiceCapitulo, indiceFrase, texto, idiomaATraducir) => {
    const url = `${process.env.REACT_APP_API_URL}/translation/deepl`;
    const authorizationHeader = { Authorization: `Bearer ${JSON.parse(localStorage?.getItem("user")).token}` };
    const response = await fetch(url, {
      method: "POST",
      body: JSON.stringify({ title: texto, lang: idiomaATraducir }),
      headers: { "Content-Type": "application/json", ...authorizationHeader },
    });
    const data = await response.json();
    const translation = data?.translations?.[0]?.text;
    if (translation) {
      editarTraduccionDeFrase(indiceCapitulo, indiceFrase, idiomaATraducir, {
        active: true,
        lang: idiomaATraducir,
        trans: translation,
      });
    }
  }, [editarTraduccionDeFrase]);

  const editarCapitulo = useCallback((indiceCapitulo, nuevoContenido) => {
    setChapters((prev) =>
      prev.map((capitulo, indice) =>
        indice === indiceCapitulo ? { ...capitulo, ...nuevoContenido } : capitulo
      )
    );
  }, []);

  const editarFraseDeCapitulo = useCallback((indiceCapitulo, indiceFrase, nuevoContenidoFrase) => {
    setChapters((prev) =>
      prev.map((capitulo, indice) =>
        indice === indiceCapitulo
          ? {
              ...capitulo,
              sentences: capitulo.sentences.map((frase, idx) =>
                idx === indiceFrase ? { ...frase, ...nuevoContenidoFrase } : frase
              ),
            }
          : capitulo
      )
    );
  }, []);

  const anadirFraseACapitulo = useCallback((indiceCapitulo, nuevaFrase) => {
    setChapters((prev) =>
      prev.map((capitulo, indice) =>
        indice === indiceCapitulo
          ? { ...capitulo, sentences: [...capitulo.sentences, nuevaFrase] }
          : capitulo
      )
    );
  }, []);

  const anadirCardAFrase = useCallback((indiceCapitulo, indiceFrase, nuevoCard) => {
    setChapters((prev) =>
      prev.map((capitulo, indice) =>
        indice === indiceCapitulo
          ? {
              ...capitulo,
              sentences: capitulo.sentences.map((frase, idx) =>
                idx === indiceFrase ? { ...frase, cards: [...(frase.cards || []), nuevoCard] } : frase
              ),
            }
          : capitulo
      )
    );
  }, []);

  const eliminarFraseDeCapitulo = useCallback((indiceCapitulo, indiceFrase) => {
    setChapters((prev) =>
      prev.map((capitulo, indice) =>
        indice === indiceCapitulo
          ? { ...capitulo, sentences: capitulo.sentences.filter((_, idx) => idx !== indiceFrase) }
          : capitulo
      )
    );
  }, []);

  const eliminarCardDeFrase = useCallback((indiceCapitulo, indiceFrase, indiceCard) => {
    setChapters((prev) =>
      prev.map((capitulo, indice) =>
        indice === indiceCapitulo
          ? {
              ...capitulo,
              sentences: capitulo.sentences.map((frase, idx) =>
                idx === indiceFrase
                  ? { ...frase, cards: (frase.cards || []).filter((_, i) => i !== indiceCard) }
                  : frase
              ),
            }
          : capitulo
      )
    );
  }, []);

  const anadirCardDespuesDeOtra = useCallback((indiceCapitulo, indiceFrase, indiceCard, nuevaCard) => {
    setChapters((prev) =>
      prev.map((capitulo, indexCap) =>
        indexCap === indiceCapitulo
          ? {
              ...capitulo,
              sentences: capitulo.sentences.map((frase, indexFrase) =>
                indexFrase === indiceFrase
                  ? {
                      ...frase,
                      cards: [
                        ...(frase.cards || []).slice(0, indiceCard + 1),
                        nuevaCard,
                        ...(frase.cards || []).slice(indiceCard + 1),
                      ],
                    }
                  : frase
              ),
            }
          : capitulo
      )
    );
  }, []);

  const cambiarCardDeFrase = useCallback((indiceCapitulo, indiceFrase, indiceCard, nuevaCard) => {
    setChapters((prev) =>
      prev.map((capitulo, indexCap) =>
        indexCap === indiceCapitulo
          ? {
              ...capitulo,
              sentences: capitulo.sentences.map((frase, indexFrase) =>
                indexFrase === indiceFrase
                  ? {
                      ...frase,
                      cards: (frase.cards || []).map((card, indexC) =>
                        indexC === indiceCard ? { ...card, ...nuevaCard } : card
                      ),
                    }
                  : frase
              ),
            }
          : capitulo
      )
    );
  }, []);

  const propsFunctions = useMemo(() => ({
    editarCapitulo,
    editarFraseDeCapitulo,
    anadirFraseACapitulo,
    anadirCardAFrase,
    editarTraduccionDeFrase,
    eliminarFraseDeCapitulo,
    handleGetContent,
    eliminarCardDeFrase,
    traducirTextoFrase,
    questions,
    setQuestions,
    myRefs,
    translateRefs,
    videoRefs,
    id,
    anadirCardDespuesDeOtra,
    cambiarCardDeFrase,
    chapters,
    activeTitleTab,
    setActiveTitleTab,
    viewLevel,
    viewPinyin,
    setViewLevel,
    setViewPinyin,
    selectedLevelFilter,
    openCardPopover,
    setOpenCardPopover,
    onCardTooltipHover: setHoveredTooltipSentenceIndex,
  }), [
    editarCapitulo,
    editarFraseDeCapitulo,
    anadirFraseACapitulo,
    anadirCardAFrase,
    editarTraduccionDeFrase,
    eliminarFraseDeCapitulo,
    handleGetContent,
    eliminarCardDeFrase,
    traducirTextoFrase,
    anadirCardDespuesDeOtra,
    cambiarCardDeFrase,
    questions,
    id,
    chapters,
    activeTitleTab,
    viewLevel,
    viewPinyin,
    selectedLevelFilter,
    openCardPopover,
  ]);

  return (
    <div className={styles.spacey}>
      <Modal visible={visibleModal} onClose={() => setVisibleModal(false)}>
        <div>
          <div className={styles.modalTitle}>{t("texts.importTextBtn")}</div>
          <div className={styles.modalDivider} />
          <div style={{ marginTop: 25 }}>
            <label className={styles.modalLabel}>{t("common.description")}</label>
            <textarea
              rows="30"
              style={{ height: "200px" }}
              value={textImport}
              onChange={(e) => setTextImport(e.target.value)}
              className={styles.input}
              placeholder={t("texts.writeHere")}
            ></textarea>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", width: "100%" }}>
            <button onClick={handleAddImportText} className={classNames("button button-small", styles.favorite)}>
              {t("texts.importTextBtn")}
            </button>
          </div>
        </div>
      </Modal>
      <Modal visible={confirmTranslateModalVisible} onClose={() => setConfirmTranslateModalVisible(false)}>
        <div style={{ padding: "8px 4px" }}>
          <div className={styles.modalTitle}>{t("texts.autoTranslate", "Auto-translate")}</div>
          <div className={styles.modalDivider} />
          <p style={{ marginTop: 18, color: "#6F767E", lineHeight: 1.5 }}>
            {t("texts.confirmAutoTranslatePrompt", "This will translate all English sentences to ES, PT, FR and RU. Do you want to continue?")}
          </p>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 22 }}>
            <button type="button" className={styles.bottomBarBtnStroke} onClick={() => setConfirmTranslateModalVisible(false)}>
              {t("common.cancel")}
            </button>
            <button
              type="button"
              className={styles.bottomBarBtnPrimary}
              disabled={loadingTranslation}
              onClick={async () => {
                setConfirmTranslateModalVisible(false);
                await handleTranslateAll();
              }}
            >
              {loadingTranslation ? "..." : "Confirm"}
            </button>
          </div>
        </div>
      </Modal>
      {!embedded && (
        <div className={styles.bottomBar}>
          <div className={styles.bottomBarRow}>
            <div className={styles.bottomBarTabs}>
              {NAVS.map((x, index) => (
                <button
                  key={index}
                  type="button"
                  className={classNames(styles.bottomBarTab, { [styles.active]: x === navSelected })}
                  onClick={() => setNavSelected(x)}
                >
                  {x === "Content" ? t("common.content") : t("texts.questions")}
                </button>
              ))}
            </div>

            <div className={styles.bottomBarSep} />

            {navSelected === "Content" && (chapters?.length > 0 || !contentLoading) && (
              <div className={styles.bottomBarChapters}>
                {chapters?.map((chapter, indexChapter) => (
                  <button
                    key={indexChapter}
                    type="button"
                    className={classNames(styles.chapterTab, {
                      [styles.chapterTabActive]: chapterIndexSelected === indexChapter,
                    })}
                    onClick={() => setChapterIndexSelected(indexChapter)}
                  >
                    {t("texts.chapter", { number: indexChapter + 1 })}
                  </button>
                ))}
                <button
                  type="button"
                  className={styles.addChapter}
                  onClick={anadirCapitulo}
                >
                  {t("texts.addChapter")}
                </button>
              </div>
            )}
            {navSelected === "Content" && (chapters?.length > 0 || !contentLoading) && <div className={styles.bottomBarSep} />}

            {navSelected === "Content" && (chapters?.length > 0 || !contentLoading) && (
              <>
                <div className={styles.filterGroup}>
                  <span className={styles.filterGroupLabel}>{t("texts.viewLevel", "View level")}</span>
                  <button type="button" className={styles.levelStepBtn} onClick={handleCycleLevelFilter}>
                    {activeLevelFilterLabel}
                  </button>
                </div>
                <div className={styles.filterGroup}>
                  <span className={styles.filterGroupLabel}>{t("texts.viewPinyin", "View pinyin")}</span>
                  <Switch value={viewPinyin} onChange={() => setViewPinyin(!viewPinyin)} />
                </div>
                <div className={styles.bottomBarSep} />
                <div className={styles.filterTranslateWrap}>
                  <div className={styles.filterTranslateIndicator} />
                  <span className={styles.filterTranslateLabel}>{t("texts.translate", "Translate")}</span>
                </div>
                <div className={styles.filterLangTabs}>
                  {LANGS.map((x) => (
                    <button
                      key={x}
                      type="button"
                      className={classNames(styles.filterLangTab, styles.filterLangTabFlag, { [styles.active]: x === activeTitleTab })}
                      onClick={() => setActiveTitleTab(x)}
                      title={LANG_LABELS[x]}
                      aria-label={LANG_LABELS[x]}
                    >
                      <img src={`/images/flags/${x}.svg`} alt="" width={24} height={18} />
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  className={styles.autoTranslateBtn}
                  disabled={loadingTranslation}
                  onClick={() => setConfirmTranslateModalVisible(true)}
                >
                  {loadingTranslation ? "..." : t("texts.autoTranslate", "Auto-translate")}
                </button>
                <div className={styles.bottomBarSep} />
              </>
            )}

            <Popover title={t("texts.textAnalysis")} actionable={<AnalysisComponent />} solidBackground>
              <div className={styles.analysisModal}>
                <div className={styles.analysisSummary}>
                  <div className={styles.analysisGrade}>
                    <span className={styles.analysisGradeLabel}>{t("texts.grade")}</span>
                    <span className={styles.analysisGradeValue}>{dataTextLevel?.analysis?.grade}</span>
                  </div>
                  <div className={styles.analysisMeta}>
                    <span>{dataTextLevel?.analysis?.avgLevel}</span>
                    <span className={styles.analysisMetaSep}>·</span>
                    <span>{dataTextLevel?.analysis?.words} {t("texts.words")}</span>
                    <span className={styles.analysisMetaSep}>·</span>
                    <span>{dataTextLevel?.analysis?.characters} {t("texts.chineseChars")}</span>
                  </div>
                </div>
                <div className={styles.analysisLevels}>
                  <div className={styles.analysisLevelsHeader}>
                    <span>{t("common.level")}</span>
                    <span>{t("texts.words")}</span>
                    <span>%</span>
                  </div>
                  {level?.map((c, idx) => (
                    <div key={idx} className={styles.analysisLevelRow}>
                      <span
                        className={styles.analysisLevelName}
                        style={{ color: LEVEL_CHART_COLORS[idx % LEVEL_CHART_COLORS.length] }}
                      >
                        {c?.title}
                      </span>
                      <span className={styles.analysisLevelWords}>{c?.value}</span>
                      <div className={styles.analysisLevelBarWrap}>
                        <div className={styles.analysisLevelBar}>
                          <div
                            className={styles.analysisLevelBarFill}
                            style={{
                              width: `${c?.percentage || 0}%`,
                              backgroundColor: LEVEL_CHART_COLORS[idx % LEVEL_CHART_COLORS.length],
                            }}
                          />
                        </div>
                        <span className={styles.analysisLevelPct}>{c?.percentage}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Popover>

            <div className={styles.bottomBarSep} />

            <button
              type="button"
              onClick={() => setVisibleModal(true)}
              className={styles.bottomBarBtnStroke}
            >
              {t("texts.importTextBtn")}
            </button>
            <button
              type="button"
              onClick={runManualContentSave}
              className={styles.bottomBarBtnPrimary}
            >
              {t("common.save")}
            </button>
            <button type="button" className={styles.bottomBarBtnStroke}>
              <Icon name="check-all" size="18" />
              {t("common.lastEdited")}{" "}
              <span>{dayjs(new Date()).format("MMM DD, YYYY - HH:mm")}</span>
            </button>
          </div>
        </div>
      )}

      <div className={styles.contentArea}>
        {navSelected === "Content" && contentLoading && chapters?.length === 0 && (
          <div style={{ padding: 24, textAlign: "center", color: "#666" }}>
            {t("common.loading", "Cargando...")}
          </div>
        )}
        {navSelected === "Content" && !contentLoading && chapters?.length === 0 && (
          <div style={{ padding: 24, textAlign: "center", color: "#666" }}>
            <p style={{ marginBottom: 16 }}>{t("texts.noChapters", "No hay capítulos.")}</p>
            <button type="button" className={styles.addChapter} onClick={anadirCapitulo}>
              {t("texts.addChapter")}
            </button>
          </div>
        )}
        {navSelected === "Content" && !(contentLoading && chapters?.length === 0) && chapters?.length > 0 && (
          <>
            {chapters?.map((chapter, i) => {
              if (i === chapterIndexSelected) {
                return (
                  <div key={i} className={styles.sentenceRows}>
                    {(chapter?.sentences || []).map((sentence, si) => (
                      <div
                        key={si}
                        className={classNames(styles.sentenceRow, hoveredTooltipSentenceIndex === si && styles.sentenceRowTooltipVisible)}
                      >
                        <div className={styles.colChinese}>
                          <Chinese chapter={chapter} chapterIndexSelected={i} {...propsFunctions} mode="sentence" sentenceIndex={si} />
                        </div>
                        <div className={styles.colVideo}>
                          <Translate chapter={chapter} chapterIndexSelected={i} {...propsFunctions} layout="video" sentenceIndex={si} videoRefs={videoRefs} activeTitleTab={activeTitleTab} />
                        </div>
                        <div className={styles.colTranslation}>
                          <Translate chapter={chapter} chapterIndexSelected={i} {...propsFunctions} layout="translation" sentenceIndex={si} translateRefs={translateRefs} activeTitleTab={activeTitleTab} />
                        </div>
                      </div>
                    ))}
                    <div className={styles.addRow}>
                      <div className={styles.colChinese}>
                        <div onClick={() => propsFunctions.anadirFraseACapitulo(i, { cards: [], processRawText: false, text: "", translations: LANGS.map((lang) => ({ active: lang === "en", lang, trans: "" })), id: nanoid() })} className={styles.addSentenceLink}>
                          + Add new sentence
                        </div>
                      </div>
                      <div className={styles.colVideo} />
                      <div className={styles.colTranslation} />
                    </div>
                  </div>
                );
              }
              return null;
            })}
          </>
        )}

        {navSelected === "Questions" && <Questions {...propsFunctions} />}
      </div>

      {/* ═══ Portal: embedded filter bar (rendered into TextEditor's filtersSlot) ═══ */}
      {embedded && filtersSlot && createPortal(
        <>
          {/* Content / Questions tabs */}
          <div className={styles.embeddedTabs}>
            {NAVS.map((x, index) => (
              <button
                key={index}
                type="button"
                className={classNames(styles.embeddedTab, { [styles.active]: x === navSelected })}
                onClick={() => setNavSelected(x)}
              >
                {x === "Content" ? t("common.content") : t("texts.questions")}
              </button>
            ))}
          </div>

          <div className={styles.embeddedSep} />

          {/* Chapters */}
          {navSelected === "Content" && (chapters?.length > 0 || !contentLoading) && (
            <>
              <div className={styles.embeddedChapters}>
                {chapters?.map((chapter, indexChapter) => (
                  <button
                    key={indexChapter}
                    type="button"
                    className={classNames(styles.embeddedChapterTab, { [styles.active]: chapterIndexSelected === indexChapter })}
                    onClick={() => setChapterIndexSelected(indexChapter)}
                  >
                    Cap {indexChapter + 1}
                  </button>
                ))}
                <button
                  type="button"
                  className={styles.embeddedChapterTab}
                  onClick={anadirCapitulo}
                >
                  +
                </button>
              </div>
              <div className={styles.embeddedSep} />
            </>
          )}

          {/* View toggles + Translation */}
          {navSelected === "Content" && chapters?.length > 0 && (
            <>
              <div className={styles.embeddedToggles}>
                <div className={styles.embeddedToggleGroup}>
                  <span className={styles.embeddedToggleLabel}>{t("texts.viewLevel", "Nivel")}</span>
                  <button type="button" className={styles.levelStepBtn} onClick={handleCycleLevelFilter}>
                    {activeLevelFilterLabel}
                  </button>
                </div>
                <div className={styles.embeddedToggleGroup}>
                  <span className={styles.embeddedToggleLabel}>Pinyin</span>
                  <Switch value={viewPinyin} onChange={() => setViewPinyin(!viewPinyin)} />
                </div>
              </div>
              <div className={styles.embeddedSep} />
              <div className={styles.embeddedTabs}>
                {LANGS.map((x) => (
                  <button
                    key={x}
                    type="button"
                    className={classNames(styles.embeddedTab, styles.embeddedTabFlag, { [styles.active]: x === activeTitleTab })}
                    onClick={() => setActiveTitleTab(x)}
                    title={LANG_LABELS[x]}
                    aria-label={LANG_LABELS[x]}
                  >
                    <img src={`/images/flags/${x}.svg`} alt="" width={24} height={18} />
                  </button>
                ))}
              </div>
              <button
                type="button"
                className={styles.autoTranslateBtn}
                disabled={loadingTranslation}
                onClick={() => setConfirmTranslateModalVisible(true)}
              >
                {loadingTranslation ? "..." : t("texts.autoTranslate", "Auto-translate")}
              </button>
              <div className={styles.embeddedSep} />
            </>
          )}

          {/* Compact analysis stats: dropdown con stats de niveles (portal evita clipping por overflow) */}
          <Popover
            portal
            solidBackground
            title={t("texts.textAnalysis")}
            actionable={
              <div className={styles.embeddedStatsBtn}>
                {level?.length > 0 && (
                  <div className={styles.embeddedStatsChart} title={level.map((c) => `${c?.title}: ${c?.percentage}%`).join(" · ")}>
                    {level.map((c, idx) => (
                      <span
                        key={idx}
                        className={styles.embeddedStatsChartSegment}
                        style={{
                          width: `${Math.max(c?.percentage || 0, 0)}%`,
                          backgroundColor: LEVEL_CHART_COLORS[idx % LEVEL_CHART_COLORS.length],
                        }}
                      />
                    ))}
                  </div>
                )}
                {dataTextLevel?.analysis?.grade && (
                  <span className={styles.embeddedStatsGrade}>
                    {dataTextLevel.analysis.grade}
                  </span>
                )}
                <div className={styles.embeddedStatsText}>
                  <span className={styles.highlight}>{dataTextLevel?.analysis?.words || 0}</span>
                  <span>{t("texts.words", "palabras")}</span>
                  <span className={styles.embeddedStatsSep}>•</span>
                  <span className={styles.highlight}>{dataTextLevel?.analysis?.characters || 0}</span>
                  <span>{t("texts.chineseChars", "字")}</span>
                </div>
              </div>
            }
          >
            <div className={styles.analysisModal}>
              <div className={styles.analysisSummary}>
                <div className={styles.analysisGrade}>
                  <span className={styles.analysisGradeLabel}>{t("texts.grade")}</span>
                  <span className={styles.analysisGradeValue}>{dataTextLevel?.analysis?.grade}</span>
                </div>
                <div className={styles.analysisMeta}>
                  <span>{dataTextLevel?.analysis?.avgLevel}</span>
                  <span className={styles.analysisMetaSep}>·</span>
                  <span>{dataTextLevel?.analysis?.words} {t("texts.words")}</span>
                  <span className={styles.analysisMetaSep}>·</span>
                  <span>{dataTextLevel?.analysis?.characters} {t("texts.chineseChars")}</span>
                </div>
              </div>
              <div className={styles.analysisLevels}>
                <div className={styles.analysisLevelsHeader}>
                  <span>{t("common.level")}</span>
                  <span>{t("texts.words")}</span>
                  <span>%</span>
                </div>
                {level?.map((c, idx) => (
                  <div key={idx} className={styles.analysisLevelRow}>
                    <span
                      className={styles.analysisLevelName}
                      style={{ color: LEVEL_CHART_COLORS[idx % LEVEL_CHART_COLORS.length] }}
                    >
                      {c?.title}
                    </span>
                    <span className={styles.analysisLevelWords}>{c?.value}</span>
                    <div className={styles.analysisLevelBarWrap}>
                      <div className={styles.analysisLevelBar}>
                        <div
                          className={styles.analysisLevelBarFill}
                          style={{
                            width: `${c?.percentage || 0}%`,
                            backgroundColor: LEVEL_CHART_COLORS[idx % LEVEL_CHART_COLORS.length],
                          }}
                        />
                      </div>
                      <span className={styles.analysisLevelPct}>{c?.percentage}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Popover>
        </>,
        filtersSlot
      )}

      {/* ═══ Portal: Import button (rendered into TextEditor's actionsSlot) ═══ */}
      {embedded && actionsSlot && createPortal(
        <>
          <button
            type="button"
            onClick={() => setVisibleModal(true)}
            className={styles.embeddedActionBtn}
          >
            {t("texts.importTextBtn")}
          </button>
          <div className={styles.embeddedSep} />
        </>,
        actionsSlot
      )}
    </div>
  );
};

export default Content2;
